import { ModalHeader } from 'src/pages/edu/Groups/components/ModalHeader';
import { NotificationTypes, _notify } from 'src/utils/notify'
import { InputTextarea } from 'primereact/inputtextarea';
import { requireField } from 'src/constants/constants';
import { LegacyModal } from 'src/components/Modal/LegacyModal'
import { Calendar } from 'src/components/Calendar';
import { useFreezeGroupMutation } from 'src/api';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import classNames from 'classnames';
import { useFormik } from 'formik';
import { FC, useRef } from 'react';

interface Props {
    visible: boolean;
    onHide: () => void;
    groupName: string;
    groupId: number;
}

interface Form {
    startAt: Date | null;
    endsAt: Date | null;
    reason: string;
}

export const FreezeModal: FC<Props> = ({
    visible,
    onHide,
    groupId,
    groupName,
}) => {
    const toast = useRef<Toast>(null);
    const [freeze, { isLoading }] = useFreezeGroupMutation();
    const formik = useFormik<Form>({
        initialValues: {
            startAt: null,
            endsAt: null,
            reason: '',
        },
        validate: data => {
            const errors: Partial<Record<keyof Form, string>> = {};

            if (!data.reason.trim()) errors.reason = requireField;
            if (!data.startAt) errors.startAt = requireField;
            if (!data.endsAt) errors.endsAt = requireField;

            if (data.startAt && data.endsAt && data.endsAt < data.startAt) {
                errors.startAt = 'Неверный временной интервал';
                errors.endsAt = 'Неверный временной интервал';
            }

            return errors;
        },
        onSubmit(data) {
            if (!data.startAt || !data.endsAt) return;

            freeze({
                id: groupId,
                reason: data.reason,
                startAt: data.startAt.getTime(),
                endsAt: data.endsAt.getTime(),
            })
                .unwrap()
                .then(() => {
                    if (!toast.current) return;
                    
                    _notify(
                        toast.current,
                        {
                            type: NotificationTypes.Notification,
                            content: 'Вы успешно заморозили группу!',
                        },
                        true
                    );
                    onHide();
                    formik.resetForm();
                })
                .catch(error => {
                    if (!toast.current) return;
                    
                    _notify(
                        toast.current,
                        {
                            type: NotificationTypes.Exception,
                            content: error.data,
                        },
                        true
                    );
                });
        },
    });

    const isFormFieldValid = (name: keyof Form) =>
        !!(formik.touched[name] && formik.errors[name]);
    const getFormErrorMessage = (name: keyof Form) => {
        return (
            isFormFieldValid(name) && (
                <small className="p-error">{formik.errors[name]}</small>
            )
        );
    };

    return (
        <>
            <LegacyModal
                visible={visible}
                header={
                    <ModalHeader>
                        Заморозка группы <span>{groupName}</span>
                    </ModalHeader>
                }
                onHide={onHide}
            >
                <form onSubmit={formik.handleSubmit}>
                    <div className="flex flex-row justify-content-between flex-wrap">
                        <div className="field w-half pl-4">
                            <span className="p-float-label">
                                <Calendar
                                    id="startAt"
                                    name="startAt"
                                    value={formik.values.startAt}
                                    maxDate={formik.values.endsAt ?? undefined}
                                    onChange={formik.handleChange}
                                    className={classNames('w-400', {
                                        'p-invalid':
                                            isFormFieldValid('startAt'),
                                    })}
                                />
                                <label
                                    htmlFor="startAt"
                                    className={classNames({
                                        'p-error': isFormFieldValid('startAt'),
                                    })}
                                >
                                    Дата начала заморозки
                                </label>
                            </span>
                            {getFormErrorMessage('startAt')}
                        </div>
                        <div className="field w-half">
                            <span className="p-float-label">
                                <Calendar
                                    id="endsAt"
                                    name="endsAt"
                                    value={formik.values.endsAt}
                                    onChange={formik.handleChange}
                                    className={classNames('w-400', {
                                        'p-invalid': isFormFieldValid('endsAt'),
                                    })}
                                />
                                <label
                                    htmlFor="endsAt"
                                    className={classNames({
                                        'p-error': isFormFieldValid('endsAt'),
                                    })}
                                >
                                    Предположительная дата разморозки
                                </label>
                            </span>
                            {getFormErrorMessage('endsAt')}
                        </div>
                    </div>
                    <div className="field w-full">
                        <span className="p-float-label">
                            <InputTextarea
                                id="reason"
                                name="reason"
                                value={formik.values.reason}
                                onChange={formik.handleChange}
                                className={classNames('w-full', {
                                    'p-invalid': isFormFieldValid('reason'),
                                })}
                            />
                            <label
                                htmlFor="reason"
                                className={classNames({
                                    'p-error': isFormFieldValid('reason'),
                                })}
                            >
                                Причина заморозки
                            </label>
                        </span>
                        {getFormErrorMessage('reason')}
                    </div>
                    <Button type="submit" loading={isLoading}>
                        Заморозить
                    </Button>
                </form>
            </LegacyModal>
            <Toast ref={toast} />
        </>
    );
};
